'use client';
// ═══════════════════════════════════════════════════════════════
// NexusSplash — intro de marque des 3 sites Nexus. ~5 s, FOND BEIGE.
// 5 variantes tirées au hasard (1×/session), toutes sur fond beige :
//   0. Assemblage   — particules dorées qui convergent + balayage de lumière
//   1. Pluie belge  — pluie d'or qui tombe et se condense en logo
//   2. Vortex       — spirale dorée qui s'enroule pour former le logo
//   3. Développement— une onde de lumière révèle le logo (comme une photo)
//   4. Constellation— réseau de points reliés qui se résout en logo (IA)
// Toutes : logo « AUREUS × NEXUS » + tagline + fondu vers le site.
// 100 % client, canvas, zéro fichier vidéo. Clic/tap = passer.
// Respecte prefers-reduced-motion (logo statique bref).
// ═══════════════════════════════════════════════════════════════
import { useEffect, useRef, useState } from 'react';

const SESSION_KEY = 'aureus_nexus_splash_v2';
const SAND = '#E9E2D3';
const SAND2 = '#DED4BE';
const INK = '#241f14';
const GOLD = '#b8933c';
const GOLD_BR = '#e7c45a';
const WORDMARK = 'AUREUS × NEXUS';
const TAGLINE = 'Le partenariat qui vend pour vous';
const N_VARIANTS = 5;

export default function NexusSplash() {
  const [show, setShow] = useState(false);
  const [fadeOut, setFadeOut] = useState(false);
  const canvasRef = useRef(null);
  const rafRef = useRef(0);
  const doneRef = useRef(false);

  useEffect(() => {
    let seen = false;
    try { seen = sessionStorage.getItem(SESSION_KEY) === '1'; } catch { seen = false; }
    if (!seen) setShow(true);
  }, []);

  const finish = () => {
    if (doneRef.current) return;
    doneRef.current = true;
    try { sessionStorage.setItem(SESSION_KEY, '1'); } catch { /* privé */ }
    setFadeOut(true);
    setTimeout(() => setShow(false), 650);
  };

  useEffect(() => {
    if (!show) return;
    const canvas = canvasRef.current;
    if (!canvas) return;
    const ctx = canvas.getContext('2d');
    const reduce = typeof window !== 'undefined' && window.matchMedia
      && window.matchMedia('(prefers-reduced-motion: reduce)').matches;

    const variant = Math.floor(Math.random() * N_VARIANTS);
    let W = 0, H = 0, DPR = Math.min(window.devicePixelRatio || 1, 2);
    let particles = [];
    let linkNext = [];
    let step = 5;

    const buildTargets = () => {
      const off = document.createElement('canvas');
      off.width = W; off.height = H;
      const o = off.getContext('2d');
      let fs = Math.max(34, Math.min(W * 0.11, 128));
      o.textAlign = 'center'; o.textBaseline = 'middle';
      o.font = `700 ${fs}px Georgia, "Times New Roman", serif`;
      while (o.measureText(WORDMARK).width > W * 0.92 && fs > 24) {
        fs -= 2; o.font = `700 ${fs}px Georgia, serif`;
      }
      o.fillStyle = '#000';
      o.fillText(WORDMARK, W / 2, H * 0.44);
      const img = o.getImageData(0, 0, W, H).data;
      const pts = [];
      step = W < 560 ? 4 : 5;
      for (let y = 0; y < H; y += step) {
        for (let x = 0; x < W; x += step) {
          if (img[(y * W + x) * 4 + 3] > 128) pts.push([x, y]);
        }
      }
      return pts;
    };

    const initPos = (p) => {
      if (variant === 1) { // pluie : tombe du haut, à l'aplomb de la cible
        return { x: p.tx + (Math.random() - 0.5) * 26, y: -Math.random() * H * 0.9 - 20 };
      }
      if (variant === 2) { // vortex : anneau large + rotation résiduelle
        const a = Math.random() * Math.PI * 2, r = Math.max(W, H) * (0.5 + Math.random() * 0.45);
        p.spin = (Math.random() < 0.5 ? 1 : -1) * (0.7 + Math.random() * 0.9);
        return { x: W / 2 + Math.cos(a) * r, y: H / 2 + Math.sin(a) * r };
      }
      if (variant === 3) return { x: p.tx, y: p.ty };           // développement : déjà en place
      if (variant === 4) return { x: p.tx + (Math.random() - 0.5) * W * 0.55, y: p.ty + (Math.random() - 0.5) * H * 0.55 };
      const a = Math.random() * Math.PI * 2, r = Math.max(W, H) * (0.35 + Math.random() * 0.5); // 0 assemblage
      return { x: W / 2 + Math.cos(a) * r, y: H / 2 + Math.sin(a) * r };
    };

    const setup = () => {
      const rect = canvas.getBoundingClientRect();
      W = Math.floor(rect.width); H = Math.floor(rect.height);
      canvas.width = W * DPR; canvas.height = H * DPR;
      ctx.setTransform(DPR, 0, 0, DPR, 0, 0);
      const targets = buildTargets();
      const goldRatio = variant === 1 ? 0.42 : 0.20;
      particles = targets.map(([tx, ty]) => {
        const p = { tx, ty, gold: Math.random() < goldRatio, r: 0.9 + Math.random() * 1.3, delay: Math.random() * 0.35, spin: 0 };
        const s = initPos(p); p.x = s.x; p.y = s.y; return p;
      });
      // Constellation : relie les points voisins consécutifs (mêmes lettres) — O(n).
      linkNext = new Array(particles.length).fill(false);
      if (variant === 4) {
        for (let i = 0; i < particles.length - 1; i++) {
          const a = particles[i], b = particles[i + 1];
          if (Math.hypot(a.tx - b.tx, a.ty - b.ty) < step * 2.6) linkNext[i] = true;
        }
      }
    };

    setup();
    const onResize = () => { cancelAnimationFrame(rafRef.current); setup(); start(); };
    window.addEventListener('resize', onResize);

    const T_ASSEMBLE = 2.0, T_SWEEP0 = 2.0, T_SWEEP1 = 3.0;
    const T_TAG0 = 2.4, T_TAG1 = 3.4, T_END = 5.0;
    const REV0 = 0.2, REV1 = 2.2; // développement
    const easeOut = (t) => 1 - Math.pow(1 - t, 3);
    let t0 = 0;

    const drawBg = () => {
      const g = ctx.createRadialGradient(W / 2, H * 0.42, 0, W / 2, H * 0.42, Math.max(W, H) * 0.75);
      g.addColorStop(0, SAND); g.addColorStop(1, SAND2);
      ctx.fillStyle = g; ctx.fillRect(0, 0, W, H);
    };

    const drawTagline = (alpha) => {
      if (alpha <= 0) return;
      ctx.globalAlpha = alpha;
      const fs = Math.max(13, Math.min(W * 0.026, 22));
      ctx.font = `600 ${fs}px Georgia, serif`;
      ctx.textAlign = 'center'; ctx.textBaseline = 'middle'; ctx.fillStyle = INK;
      const ty = H * 0.44 + Math.min(W * 0.11, 128) * 0.62 + 18;
      ctx.save(); ctx.translate(W / 2, ty);
      try { ctx.letterSpacing = '3px'; } catch { /* vieux navigateurs */ }
      ctx.fillText(TAGLINE.toUpperCase(), 0, 0); ctx.restore();
      const lw = Math.min(W * 0.34, 300) * alpha;
      ctx.strokeStyle = GOLD; ctx.lineWidth = 2;
      ctx.beginPath(); ctx.moveTo(W / 2 - lw / 2, ty + fs * 1.15); ctx.lineTo(W / 2 + lw / 2, ty + fs * 1.15); ctx.stroke();
      ctx.globalAlpha = 1;
    };

    const px = []; const py = []; const pv = [];

    const frame = (ts) => {
      if (!t0) t0 = ts;
      const t = (ts - t0) / 1000;
      drawBg();

      const sweepX = W * (-0.1 + 1.2 * Math.min(1, Math.max(0, (t - T_SWEEP0) / (T_SWEEP1 - T_SWEEP0))));
      const band = W * 0.14;
      const revX = W * Math.min(1, Math.max(0, (t - REV0) / (REV1 - REV0)));      // développement
      const ringR = Math.max(W, H) * Math.min(1, Math.max(0, (t - 0.1) / 1.9));   // vortex halo

      // Pass 1 — positions + visibilité
      for (let i = 0; i < particles.length; i++) {
        const p = particles[i];
        const lt = Math.min(1, Math.max(0, (t - p.delay) / (T_ASSEMBLE - p.delay)));
        const e = easeOut(lt);
        let x = p.x + (p.tx - p.x) * e;
        let y = p.y + (p.ty - p.y) * e;
        if (variant === 2 && e < 1) { // rotation résiduelle (spirale)
          const ang = p.spin * (1 - e) * Math.PI;
          const dx = x - W / 2, dy = y - H / 2, ca = Math.cos(ang), sa = Math.sin(ang);
          x = W / 2 + dx * ca - dy * sa; y = H / 2 + dx * sa + dy * ca;
        }
        px[i] = x; py[i] = y;

        let vis = true, col = p.gold ? GOLD : INK, r = p.r, alpha = 0.35 + 0.65 * e;
        if (variant === 3) { // développement : révélé par la bande, glow au passage
          if (p.tx > revX) { vis = false; }
          else { alpha = 1; if (revX - p.tx < band) { col = GOLD_BR; r = p.r + 1.0 * (1 - (revX - p.tx) / band); } }
        } else if (variant === 0 && t >= T_SWEEP0 && t <= T_SWEEP1 + 0.2 && Math.abs(x - sweepX) < band) {
          const k = 1 - Math.abs(x - sweepX) / band; col = GOLD_BR; r = p.r + 1.1 * k;
        } else if (variant === 2) { // halo circulaire qui balaie
          const d = Math.abs(Math.hypot(x - W / 2, y - H / 2) - ringR);
          if (d < band * 0.6) { const k = 1 - d / (band * 0.6); col = GOLD_BR; r = p.r + 1.0 * k; }
        } else if (variant === 1 && lt > 0.9) { // pluie : léger éclat en se posant
          col = p.gold ? GOLD_BR : GOLD;
        }
        pv[i] = vis ? alpha : 0;
        ctx.globalAlpha = pv[i]; ctx.fillStyle = col;
        if (pv[i] > 0) { ctx.beginPath(); ctx.arc(x, y, r, 0, Math.PI * 2); ctx.fill(); }
      }
      ctx.globalAlpha = 1;

      // Constellation : liens entre voisins pendant l'assemblage
      if (variant === 4 && t < T_ASSEMBLE + 0.5) {
        const la = Math.max(0, 1 - t / (T_ASSEMBLE + 0.5));
        ctx.strokeStyle = `rgba(184,147,60,${0.35 * la})`; ctx.lineWidth = 1;
        ctx.beginPath();
        for (let i = 0; i < particles.length - 1; i++) {
          if (!linkNext[i]) continue;
          ctx.moveTo(px[i], py[i]); ctx.lineTo(px[i + 1], py[i + 1]);
        }
        ctx.stroke();
      }

      drawTagline(Math.min(1, Math.max(0, (t - T_TAG0) / (T_TAG1 - T_TAG0))));
      if (t >= T_END) { finish(); return; }
      rafRef.current = requestAnimationFrame(frame);
    };

    const frameReduced = () => {
      drawBg();
      for (const p of particles) { ctx.fillStyle = p.gold ? GOLD : INK; ctx.beginPath(); ctx.arc(p.tx, p.ty, p.r, 0, Math.PI * 2); ctx.fill(); }
      drawTagline(1);
      setTimeout(finish, 1400);
    };

    const start = () => { t0 = 0; if (reduce) frameReduced(); else rafRef.current = requestAnimationFrame(frame); };
    start();
    const safety = setTimeout(finish, 6000);

    return () => {
      cancelAnimationFrame(rafRef.current);
      clearTimeout(safety);
      window.removeEventListener('resize', onResize);
    };
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [show]);

  if (!show) return null;

  return (
    <div
      onClick={finish}
      role="button"
      aria-label="Passer l'intro"
      style={{
        position: 'fixed', inset: 0, zIndex: 9999, cursor: 'pointer', background: SAND,
        opacity: fadeOut ? 0 : 1, transition: 'opacity 620ms ease',
      }}
    >
      <canvas ref={canvasRef} style={{ width: '100%', height: '100%', display: 'block' }} />
      <div style={{
        position: 'absolute', bottom: 22, left: 0, right: 0, textAlign: 'center',
        fontFamily: 'ui-monospace, Menlo, monospace', fontSize: 11, letterSpacing: '.18em',
        textTransform: 'uppercase', color: 'rgba(36,31,20,.45)', pointerEvents: 'none',
      }}>
        Toucher pour passer
      </div>
    </div>
  );
}
